/**
 * Hook de estado del nivel del módulo english. Centraliza el `GET /state` y las
 * mutaciones (`POST /level`, `POST /triage`), y lo consumen el chip del Home, la
 * pantalla de Evolución, el control de intensidad de los ajustes y el reveal del
 * examen.
 *
 * Regla de intensidad (ver docs/speak-plan/10): la intensidad EFECTIVA con la
 * que habla el coach es `state.effectiveIntensity` SIEMPRE. La `recommended` se
 * muestra como guía. Elegir una intensidad a mano implica pasar a manual, así
 * que `setIntensity` manda `{ intensity, levelAuto: false }` junto.
 */
import { useCallback, useEffect, useState } from 'react';
import type { EnglishIntensity } from '@orborix/shared-types';
import {
  activateLevelExplore,
  deactivateLevelExplore,
  EnglishApiError,
  getEnglishState,
  postLevel,
  selfSetLevel,
  setPaThreshold as apiSetPaThreshold,
  type EnglishState,
  type ExploreCefrLevel,
} from '../../lib/englishApi.js';
import { createSessionCache } from '../../lib/sessionCache.js';

// Caché de sesión (stale-while-revalidate): la última lectura de `/state`
// sobrevive al unmount, así una revisita (p.ej. volver al Home) monta con
// datos al instante mientras el fetch de fondo revalida. Se limpia al cambiar
// de usuario (AuthProvider → clearSessionCaches).
const stateCache = createSessionCache<EnglishState>();

/**
 * Invalida el caché de `/state`. Llamar tras una mutación HECHA FUERA del hook
 * que cambia el estado server-side (postTriage, postCalibrationConfirm): si el
 * caché retuviera `onboarded: false` después del triage, el gate del Home
 * redirigiría al onboarding en loop antes de que la revalidación llegue.
 */
export function invalidateEnglishStateCache(): void {
  stateCache.set(null);
}

/**
 * Cada lectura fresca de `/state`, para quien necesita el nivel de la cuenta
 * sin montar este hook: el `ListenProvider` vive por encima de las rutas y
 * monta una sola vez, muchas veces en /login y sin sesión (su propio pedido da
 * 401). Así se entera del nivel cuando el alumno entra, termina el onboarding
 * o cambia de nivel, sin pedir `/state` de nuevo.
 */
const freshStateListeners = new Set<(state: EnglishState) => void>();

export function subscribeEnglishState(fn: (state: EnglishState) => void): () => void {
  freshStateListeners.add(fn);
  return () => {
    freshStateListeners.delete(fn);
  };
}

function rememberFresh(next: EnglishState): void {
  stateCache.set(next);
  for (const fn of freshStateListeners) fn(next);
}

export interface UseEnglishLevel {
  state: EnglishState | null;
  loading: boolean;
  /** Error de red/servidor al leer el estado (null = OK). */
  error: EnglishApiError | null;
  /** Re-lee `/state` (p.ej. tras volver del examen). */
  refresh: () => Promise<void>;
  /** Fija la intensidad a mano (pasa a manual). Optimista + persiste. */
  setIntensity: (intensity: EnglishIntensity) => Promise<void>;
  /** Prende/apaga el ajuste automático global. Optimista + persiste. */
  setLevelAuto: (levelAuto: boolean) => Promise<void>;
  /** Fija la sensibilidad de pronunciación (30-90). Optimista + persiste. */
  setPaThreshold: (accuracyThreshold: number) => Promise<void>;
  /**
   * Activa el overlay de exploración (plan 38). Invalida el caché + re-lee
   * `/state` para que TabBar/gates/Profile vean el overlay. Propaga
   * `EnglishApiError` (409 con `.code`) para que el caller muestre el copy.
   */
  startExplore: (level: ExploreCefrLevel) => Promise<void>;
  /** Desactiva el overlay → vuelve al nivel real. Invalida caché + re-lee. */
  stopExplore: () => Promise<void>;
  /**
   * Promoción self-set (plan 38 F4): fija el nivel REAL. Invalida caché + re-lee.
   * Propaga `EnglishApiError` (cooldown → `.details.retryAt`) al caller.
   */
  selfSet: (level: ExploreCefrLevel, applyRecommended?: boolean) => Promise<void>;
}

export function useEnglishLevel(): UseEnglishLevel {
  const cached = stateCache.get();
  const [state, setState] = useState<EnglishState | null>(cached);
  const [loading, setLoading] = useState(cached === null);
  const [error, setError] = useState<EnglishApiError | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const next = await getEnglishState();
      rememberFresh(next);
      setState(next);
      setError(null);
    } catch (err) {
      setError(err instanceof EnglishApiError ? err : new EnglishApiError('unknown', 0, 'unknown'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void getEnglishState()
      .then((next) => {
        // Cachear aunque el componente ya se haya desmontado: el dato es
        // igual de fresco y le sirve al próximo mount.
        rememberFresh(next);
        if (!cancelled) {
          setState(next);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof EnglishApiError ? err : new EnglishApiError('unknown', 0, 'unknown'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Updates optimistas: el caché de sesión se actualiza junto con el estado
  // local, para que otra pantalla que monte mientras tanto no vea el valor viejo.
  const applyOptimistic = useCallback((patch: Partial<EnglishState>): void => {
    setState((s) => {
      if (!s) return s;
      const next = { ...s, ...patch };
      stateCache.set(next);
      return next;
    });
  }, []);

  const setIntensity = useCallback(async (intensity: EnglishIntensity): Promise<void> => {
    // Optimista: elegir intensidad = manual.
    applyOptimistic({ effectiveIntensity: intensity, levelAuto: false });
    await postLevel({ intensity, levelAuto: false });
    await load();
  }, [applyOptimistic, load]);

  const setLevelAuto = useCallback(async (levelAuto: boolean): Promise<void> => {
    applyOptimistic({ levelAuto });
    await postLevel({ levelAuto });
    await load();
  }, [applyOptimistic, load]);

  const setPaThreshold = useCallback(async (accuracyThreshold: number): Promise<void> => {
    // Optimista: el chip se marca al instante; si el POST falla, `load()` en el
    // catch del caller no corre — pero re-sincronizamos en el próximo refresh.
    applyOptimistic({ paAccuracyThreshold: accuracyThreshold });
    await apiSetPaThreshold(accuracyThreshold);
  }, [applyOptimistic]);

  const startExplore = useCallback(async (level: ExploreCefrLevel): Promise<void> => {
    // Sin optimista: el server valida (409 si A0 / mismo nivel / no onboardeado /
    // en welcome) y el badge + features pausadas dependen del `levelOverlay`
    // real. Invalidar + re-leer garantiza que TabBar/gates/Profile (que leen el
    // MISMO caché de sesión) vean el overlay — mismo bug de loop que `onboarded`.
    await activateLevelExplore(level);
    invalidateEnglishStateCache();
    await load();
  }, [load]);

  const stopExplore = useCallback(async (): Promise<void> => {
    await deactivateLevelExplore();
    invalidateEnglishStateCache();
    await load();
  }, [load]);

  const selfSet = useCallback(
    async (level: ExploreCefrLevel, applyRecommended = false): Promise<void> => {
      // El write ES la feature (a diferencia del catch vacío de LevelResult): si
      // falla, propagamos para que el caller muestre el error (cooldown, etc.).
      await selfSetLevel(level, applyRecommended);
      invalidateEnglishStateCache();
      await load();
    },
    [load],
  );

  return {
    state,
    loading,
    error,
    refresh: load,
    setIntensity,
    setLevelAuto,
    setPaThreshold,
    startExplore,
    stopExplore,
    selfSet,
  };
}
